import { useRef } from 'react';
import { Rect } from 'react-konva';
import { jsPDF } from 'jspdf';
import {
  resetPageCanvas,
  saveCanvasImage,
} from '../api/pageApi';

// Saved rendered images and PDF capture are separate from the live editor.
export function useCanvasOutputController({
  canvasSize,
  describeLayoutItem,
  effectiveStageScale,
  getCurrentTextValues,
  id,
  logLayoutDebug,
  notify,
  pageData,
  pageLanguage,
  saveTextItems,
  setPageData,
  setRenderingPreview,
  setSavingPage,
  setRemovingRenderedPage,
  setSelectedId,
  setSelectedTextItemId,
  stageRef,
  textItemsRef,
}) {
  const activePage = useRef('');
  const pageKey = `${id}:${pageLanguage}`;
  activePage.current = pageKey;
  const resetPreviewImage = async () => {
    setRemovingRenderedPage(true);
    try {
      const response = await resetPageCanvas(id, pageLanguage);
      if (!response.data.success) throw new Error('Render removal failed');
      const draftWording = Object.fromEntries(Object.entries(getCurrentTextValues()).map(([language, text]) => [`text_${language}`, text]));
      setPageData((current) => current?._id === id
        ? { ...current, ...draftWording, [`image_preview_${pageLanguage}`]: null } : current);
      notify('Rendered page removed. Your editable page was kept.');
      return true;
    } catch (error) {
      console.error('Could not remove rendered page', error);
      notify('Could not remove the rendered page.', 'error');
      return false;
    } finally { setRemovingRenderedPage(false); }
  };

  const saveCanvasAsImage = async (language, { showNotification = true } = {}) => {
    if (!stageRef.current || !pageData) {
      notify('The page is not ready to render.', 'error');
      return false;
    }
    const stage = stageRef.current;
    logLayoutDebug('preview-save-start', {
      language,
      pageId: id,
      stage: { width: stage.width(), height: stage.height(), scaleX: stage.scaleX(), scaleY: stage.scaleY() },
      items: textItemsRef.current.map(describeLayoutItem),
    });
    setRenderingPreview(true);
    let blob;
    const currentScale = effectiveStageScale;
    const editorNodes = stage.find('.editor-only');
    const editorVisibility = editorNodes.map((node) => node.visible());
    try {
      editorNodes.forEach((node) => node.hide());
      stage.scale({ x: 1, y: 1 });
      stage.position({ x: 0, y: 0 });
      stage.draw();
      blob = await stage.toBlob({ pixelRatio: 1, mimeType: 'image/png' });
      if (!blob) throw new Error('Canvas returned no image');
    } catch (error) {
      console.error('Failed to render canvas:', error);
      notify('The page could not be rendered.', 'error');
      setRenderingPreview(false);
      return false;
    } finally {
      editorNodes.forEach((node, index) => node.visible(editorVisibility[index]));
      stage.scale({ x: currentScale, y: currentScale });
      stage.position({ x: 0, y: 0 });
      stage.draw();
    }

    const formData = new FormData();
    formData.append('image', blob, `page_${language}_${id}.png`);
    formData.append('page_id', id);
    formData.append('language', language);
    formData.append('book_id', pageData.book_id);
    try {
      const response = await saveCanvasImage(formData);
      if (response.status !== 200) {
        notify('Could not save the rendered page.', 'error');
        return false;
      }
      const draftWording = Object.fromEntries(Object.entries(getCurrentTextValues()).map(([code, text]) => [`text_${code}`, text]));
      setPageData((current) => current?._id === id ? {
        ...current, ...draftWording,
        [`image_preview_${language}`]: response.data?.url || current?.[`image_preview_${language}`],
      } : current);
      if (showNotification) notify('Rendered page saved.');
      logLayoutDebug('preview-save-finished', { language, previewUrl: response.data?.url });
      return true;
    } catch (error) {
      console.error('Error saving canvas image:', error);
      notify('Could not save the rendered page.', 'error');
      return false;
    } finally {
      setRenderingPreview(false);
    }
  };

  const savePage = async () => {
    setSavingPage(true);
    const stage = stageRef.current;
    try {
      const savedLayout = await saveTextItems(
        textItemsRef.current,
        { notifyMessage: null, preserveLocalItems: true },
      );
      if (!savedLayout) return;
      if (activePage.current !== pageKey || stageRef.current !== stage) {
        notify('The page or language changed. Render the current page again.', 'error');
        return;
      }
      const previewSaved = await saveCanvasAsImage(pageLanguage, { showNotification: false });
      if (previewSaved) notify('Rendered page saved. Text boxes remain editable.');
    } finally {
      setSavingPage(false);
    }
  };

  const exportToPDF = () => {
    const stage = stageRef.current;
    if (!stage || !pageData) return;
    const currentScale = effectiveStageScale;
    const editorNodes = stage.find('.editor-only');
    const editorVisibility = editorNodes.map((node) => node.visible());
    let dataURL;
    try {
      editorNodes.forEach((node) => node.hide());
      stage.scale({ x: 1, y: 1 });
      stage.position({ x: 0, y: 0 });
      stage.draw();
      dataURL = stage.toDataURL({ pixelRatio: 300 / 96 });
    } finally {
      editorNodes.forEach((node, index) => node.visible(editorVisibility[index]));
      stage.scale({ x: currentScale, y: currentScale });
      stage.position({ x: 0, y: 0 });
      stage.draw();
    }
    const widthInInches = pageData.width / 300;
    const heightInInches = pageData.height / 300;
    const pdf = new jsPDF({
      orientation: widthInInches > heightInInches ? 'landscape' : 'portrait',
      unit: 'in',
      format: [widthInInches, heightInInches],
    });
    pdf.addImage(dataURL, 'PNG', 0, 0, widthInInches, heightInInches, undefined, 'FAST');
    pdf.save(`${pageData.name}_300dpi.pdf`);
  };

  const handleStageClick = (event) => {
    // The artwork image fills the stage, so blank page clicks target that image.
    // Other canvas objects keep their own selection behavior.
    const target = event?.target;
    if (target && target !== target.getStage?.() && !target.hasName?.('page-background')) return;
    setSelectedId(null);
    setSelectedTextItemId(null);
  };

  const renderGridLines = () => {
    const lines = [];
    for (let x = 50; x < canvasSize.width; x += 50) {
      lines.push(<Rect name="editor-only" key={`v-${x}`} x={x} y={0} width={1} height={canvasSize.height} fill="rgba(0,0,0,0.1)" listening={false} />);
    }
    for (let y = 50; y < canvasSize.height; y += 50) {
      lines.push(<Rect name="editor-only" key={`h-${y}`} x={0} y={y} width={canvasSize.width} height={1} fill="rgba(0,0,0,0.1)" listening={false} />);
    }
    return lines;
  };

  return {
    exportToPDF,
    handleStageClick,
    renderGridLines,
    resetPreviewImage,
    savePage,
  };
}
